import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import './StatWidget.css';
import NumberInput from '../utils/NumberInput';
import Tooltip from '../utils/Tooltip';
import CriterionWeight from '../generator/CriterionWeight';
import * as stats from '../../core/st';
import { pctToColor, pctToGaugeGradient } from '../../utils/colorUtils';

const POWER_ICON = stats.STATS.find((s) => s.id === stats.POWER_ID)?.icon ?? '/img/stat_puissance.png';

const TXT_INFINITY = { fr: String.fromCharCode(0x221E), en: String.fromCharCode(0x221E) };
const TXT_INFINITY_ARIA = {
    fr: 'Maximiser cette stat (infini)',
    en: 'Maximize this stat (infinity target)',
    de: 'Diese Stat maximieren (unendlich)',
    es: 'Maximizar esta stat (infinito)',
    pt: 'Maximizar esta stat (infinito)',
};
const TXT_INFINITY_TO_TARGET_ARIA = {
    fr: 'Passer en cible numérique',
    en: 'Switch to numeric target',
    de: 'Zu numerischem Ziel wechseln',
    es: 'Cambiar a objetivo numérico',
    pt: 'Mudar para alvo numérico',
};

const TXT_STAT_TARGET_CB_ARIA = {
    fr: 'Inclure comme critère de génération',
    en: 'Include as generator target',
    de: 'Als Generierungs-Kriterium einbeziehen',
    es: 'Incluir como criterio de generación',
    pt: 'Incluir como critério de geração',
};

const StatWidget = ({
    stat,
    powerValue = 0,
    damageValue = 0,
    critDamageValue = 0,
    hasActiveElementalTargets = false,
    hasActiveElementalDamageTargets = false,
    onBaseValueChange,
    onScrollValueChange,
    onDesiredValueChange,
    onExitMaximizeToValue,
    onMaximizeStatChange,
    showWeight = false,
    weight = 1,
    onWeightChange,
    lang,
    genConfig,
    setGenConfig,
    hasSpecialParams = true,
    reserveAdditionalColumn = false,
    isGenerating = false,
    mobileTargetCheckbox = false,
    compactRow = false,
    canEditBuild = true,
    canEditGenerator = true,
    characterPoints = 0,
}) => {
    const {
        name,
        id,
        icon,
        current = 0,
        desired: committedDesired = null,
        minRange = 0,
        maxRange = 0,
        category,
        base = 0,
        step = 1,
        maximize: isMaximize = false,
        unit = '',
    } = stat;

    const label_value = stats.getStatAlias(name, lang);

    const intMinRange = parseInt(minRange, 10);
    const intMaxRange = parseInt(maxRange, 10);
    // The slider's leftmost notch, one step below the minimum, is "no target".
    // It keeps the minimum itself free to be a target (Resistance ≥ 0).
    const offNotch = intMinRange - step;

    // A drag stays inside this row until it is released: `pending` (a number,
    // or null on the off notch) drives the thumb, the box and the gauge, and
    // the build only hears about it on release. Committing every tick
    // re-rendered the whole generator — stats, score, criteria chips — and
    // saved the build, which is what made dragging stutter.
    const [pending, setPending] = useState(undefined);
    const desired = pending !== undefined ? pending : committedDesired;
    const hasTarget = desired !== null && desired !== undefined && Number.isFinite(Number(desired));
    const sliderValue = hasTarget ? Number(desired) : offNotch;
    const desiredRef = useRef(null);
    const [pendingSelectDesired, setPendingSelectDesired] = useState(false);

    // Check if this is an elemental stat that can benefit from power
    const isElementalStat = id === stats.STRENGTH_ID || id === stats.INTELLIGENCE_ID ||
        id === stats.CHANCE_ID || id === stats.AGILITY_ID;
    // Elemental damage stats: generic "do" counts in total (Dofus base behavior). Crit/power are generator options only.
    const isElementalDamageStat = id === 92 || id === 88 || id === 89 || id === 90 || id === 91; // neutral, earth, fire, water, air
    const allowPower = genConfig?.allowPower ?? false;
    const critDamCountsAsDamage = genConfig?.critDamCountsAsDamage ?? false;
    // Display value: elemental damage = elemental + generic do (always). No crit, no power.
    const displayValue = useMemo(() => {
        if (isElementalDamageStat) return current + damageValue;
        return current;
    }, [isElementalDamageStat, current, damageValue]);
    // Effective for gauge/target: includes power (elemental stats), generic do (elemental damage), crit when generator options on
    const effectiveCurrent = useMemo(() => {
        let effective = current;
        if (allowPower && isElementalStat) effective += powerValue;
        if (isElementalDamageStat) {
            effective += damageValue; // generic do always (Dofus base)
            if (critDamCountsAsDamage) effective += critDamageValue;
        }
        if (critDamCountsAsDamage && (id === stats.DAMAGE_ID)) effective += critDamageValue;
        return effective;
    }, [allowPower, critDamCountsAsDamage, isElementalStat, isElementalDamageStat, current, powerValue, damageValue, critDamageValue, id]);

    const targetPct = useMemo(() => {
        if (isMaximize || !hasTarget) return 100;
        if (desired <= 0) return effectiveCurrent >= desired ? 100 : 0;
        return Math.min((effectiveCurrent / desired) * 100, 100);
    }, [effectiveCurrent, desired, hasTarget, isMaximize]);

    const isAttribute = category === "attribute" && id !== stats.POWER_ID;
    const maxBase = isAttribute
        ? stats.investedPointsToStatValue(
              null,
              id,
              Math.max(0, Number.isFinite(characterPoints) ? characterPoints : 0) +
                  stats.calculateTotalCost(id, Number.isFinite(base) ? base : 0)
          )
        : undefined;
    const isPower = id === stats.POWER_ID;
    const isDamage = id === stats.DAMAGE_ID;

    const isGaugeInactive = !isMaximize && !hasTarget;
    const hasActiveFiniteTarget = !isMaximize && hasTarget;
    const isRowActive = isMaximize || hasActiveFiniteTarget;
    // The gauge shares the slider's scale, off notch included, so a met
    // target's fill reaches the thumb.
    const gaugeSpan = Math.max(intMaxRange - offNotch, 1);
    const gaugePct = (value) => Math.min(Math.max((value / gaugeSpan) * 100, 0), 100);

    // Click outside the gauge cycles: inactive -> maximize -> inactive. An
    // active numeric target clears the criterion instead of jumping straight
    // to maximize, so turning one off stays a single click.
    const handleStatInfoClick = useCallback(() => {
        if (mobileTargetCheckbox || isGenerating || !onMaximizeStatChange) return;
        if (isMaximize) onMaximizeStatChange(id, false);
        else if (hasActiveFiniteTarget) onDesiredValueChange?.(id, null);
        else onMaximizeStatChange(id, true);
    }, [hasActiveFiniteTarget, id, isGenerating, isMaximize, mobileTargetCheckbox, onDesiredValueChange, onMaximizeStatChange]);

    const isInteractiveTarget = useCallback((target, container) => {
        if (!(target instanceof Element)) return false;
        const interactiveAncestor = target.closest(
            'button, input, select, textarea, a, label, [role="button"], [role="checkbox"], [contenteditable="true"]'
        );
        if (!interactiveAncestor) return false;
        return interactiveAncestor !== container;
    }, []);

    const handleRowClick = useCallback(
        (e) => {
            if (mobileTargetCheckbox || !canEditGenerator || isGenerating) return;
            if (isInteractiveTarget(e.target, e.currentTarget)) return;
            handleStatInfoClick();
        },
        [canEditGenerator, handleStatInfoClick, isGenerating, isInteractiveTarget, mobileTargetCheckbox]
    );

    const handleTargetCheckboxChange = useCallback(
        (e) => {
            if (isGenerating || !onMaximizeStatChange) return;
            const on = e.target.checked;
            if (on) {
                onMaximizeStatChange(id, true);
            } else if (isMaximize) {
                onMaximizeStatChange(id, false);
            } else if (onDesiredValueChange) {
                onDesiredValueChange(id, null);
            }
        },
        [id, isGenerating, isMaximize, onDesiredValueChange, onMaximizeStatChange]
    );

    const handleStatInfoKeyDown = useCallback(
        (e) => {
            if (e.target !== e.currentTarget && isInteractiveTarget(e.target, e.currentTarget)) return;
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            handleStatInfoClick();
        },
        [handleStatInfoClick, isInteractiveTarget]
    );

    const handleSliderPointerDown = useCallback(() => {
        if (!isMaximize || isGenerating || !onExitMaximizeToValue) return;
        const seed = Math.min(intMaxRange, Math.max(intMinRange + step, Math.round(effectiveCurrent)));
        onExitMaximizeToValue(id, seed);
    }, [effectiveCurrent, id, intMaxRange, intMinRange, isGenerating, isMaximize, onExitMaximizeToValue, step]);

    const getTargetSeed = useCallback(
        () => Math.min(intMaxRange, Math.max(intMinRange + step, Math.round(effectiveCurrent))),
        [effectiveCurrent, intMaxRange, intMinRange, step]
    );

    const handleInfinityTargetClick = useCallback(() => {
        if (!isMaximize || isGenerating) return;
        const seed = getTargetSeed();
        setPendingSelectDesired(true);
        if (onExitMaximizeToValue) {
            onExitMaximizeToValue(id, seed);
            return;
        }
        onDesiredValueChange?.(id, seed);
        onMaximizeStatChange?.(id, false);
    }, [getTargetSeed, id, isGenerating, isMaximize, onDesiredValueChange, onExitMaximizeToValue, onMaximizeStatChange]);

    const handleSliderChange = useCallback(
        (e) => {
            let newValue = parseInt(e.target.value, 10);
            if (!Number.isFinite(newValue)) newValue = offNotch;
            if (isMaximize) {
                if (!onExitMaximizeToValue) return;
                const seed = Math.min(intMaxRange, Math.max(intMinRange + step, newValue));
                onExitMaximizeToValue(id, seed);
                return;
            }
            setPending(newValue < intMinRange ? null : newValue);
        },
        [id, intMaxRange, intMinRange, isMaximize, offNotch, onExitMaximizeToValue, step]
    );

    const commitPending = useCallback(() => {
        if (pending === undefined) return;
        setPending(undefined);
        const committed = Number.isFinite(Number(committedDesired)) && committedDesired !== null
            ? Number(committedDesired)
            : null;
        if (pending !== committed) onDesiredValueChange(id, pending);
    }, [committedDesired, id, onDesiredValueChange, pending]);

    const handleTextBoxChange = useCallback(
        (newValue) => {
            // An emptied box clears the target, like the slider's off notch.
            const isOff = !Number.isFinite(newValue);
            setPending(undefined);
            onDesiredValueChange(id, isOff ? null : newValue);
        },
        [id, onDesiredValueChange]
    );

    const handleBaseValueChange = useCallback((newValue) => {
        onBaseValueChange(id, Number.isFinite(newValue) ? newValue : 0);
    }, [id, onBaseValueChange]);

    // Mobile: the whole row is the select toggle. Selecting defaults to maximize
    // (∞), except AP/MP which default to a specific target at their max value.
    // Deselecting clears the criterion. The actual target/∞ editing happens in
    // the gen bar, not here.
    const handleMobileSelectToggle = useCallback(() => {
        if (isGenerating || !canEditGenerator) return;
        if (isRowActive) {
            if (isMaximize) onMaximizeStatChange?.(id, false);
            else onDesiredValueChange?.(id, null);
            return;
        }
        if (id === stats.AP_ID || id === stats.MP_ID) {
            if (onExitMaximizeToValue) onExitMaximizeToValue(id, intMaxRange);
            else onDesiredValueChange?.(id, intMaxRange);
        } else {
            onMaximizeStatChange?.(id, true);
        }
    }, [canEditGenerator, id, intMaxRange, isGenerating, isMaximize, isRowActive, onDesiredValueChange, onExitMaximizeToValue, onMaximizeStatChange]);

    const handleMobileRowClick = useCallback(
        (e) => {
            if (!canEditGenerator || isGenerating) return;
            if (isInteractiveTarget(e.target, e.currentTarget)) return;
            handleMobileSelectToggle();
        },
        [canEditGenerator, handleMobileSelectToggle, isGenerating, isInteractiveTarget]
    );

    const handleMobileRowKeyDown = useCallback(
        (e) => {
            if (e.target !== e.currentTarget && isInteractiveTarget(e.target, e.currentTarget)) return;
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            handleMobileSelectToggle();
        },
        [handleMobileSelectToggle, isInteractiveTarget]
    );

    // A change from elsewhere (gen bar, reset, another build) wins over a drag.
    useEffect(() => {
        setPending(undefined);
    }, [committedDesired]);

    useEffect(() => {
        if (!pendingSelectDesired || isMaximize) return;
        const input = desiredRef.current?.querySelector?.('input');
        if (!input) return;
        const raf = window.requestAnimationFrame(() => {
            input.focus();
            if (typeof input.select === 'function') input.select();
            setPendingSelectDesired(false);
        });
        return () => window.cancelAnimationFrame(raf);
    }, [isMaximize, pendingSelectDesired]);

    const statInfoClickable = !mobileTargetCheckbox && canEditGenerator;
    const isRowClickable = statInfoClickable && !isGenerating;
    const showGeneratorControls = canEditGenerator;
    const showActiveTargetStyling = showGeneratorControls;

    const renderTooltipContent = () => {
        const breakdown = stat.breakdown || [];
        const showGenericDamage = isElementalDamageStat && damageValue !== 0;
        if (breakdown.length === 0 && !showGenericDamage) return null;

        const genericDamageLabel = stats.getStatLabel('do', lang) || (lang === 'en' ? 'Damage' : 'Dommages');

        return (
            <div className="stat-breakdown-tooltip">
                {breakdown.map((item, idx) => {
                    const itemName = typeof item.name === 'object' 
                        ? (item.name[lang] || item.name.en || item.name.fr || 'Item')
                        : item.name;
                    return (
                        <div key={idx} className="stat-breakdown-row">
                            <span className="stat-breakdown-name">{itemName}</span>
                            <span className="stat-breakdown-value">{item.value > 0 ? `+${item.value}` : item.value}</span>
                        </div>
                    );
                })}
                {showGenericDamage ? (
                    <div className="stat-breakdown-row">
                        <span className="stat-breakdown-name">{genericDamageLabel}</span>
                        <span className="stat-breakdown-value">{damageValue > 0 ? `+${damageValue}` : damageValue}</span>
                    </div>
                ) : null}
            </div>
        );
    };

    // Base characteristic input (attributes and power).
    // Shared by the desktop widget and the simplified mobile row.
    const additionalBlock = (
        <div className={`stat-additional${!(isAttribute || isPower) ? ' stat-additional--empty' : ''}`}>
            {isAttribute || isPower ? (
                <div className={`stat-special-params ${!hasSpecialParams ? 'empty' : ''}`}>
                    {isAttribute && (
                        <div className={`attribute-input ${isGaugeInactive ? 'inactive' : ''}`}>
                            <NumberInput
                                value={Number.isFinite(base) ? base : ''}
                                onValueChange={handleBaseValueChange}
                                displayZero={true}
                                size={3}
                                min={0}
                                max={maxBase}
                                allowNegative={false}
                                disabled={isGenerating || !canEditBuild}
                            />
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );

    const isZeroValue = !Number.isFinite(displayValue) || displayValue === 0;

    const currentBlock = (
        <div
            className={`stat-data ${showActiveTargetStyling && isPower && allowPower && isGaugeInactive && hasActiveElementalTargets ? 'power-contributing' : ''} ${showActiveTargetStyling && isDamage && isGaugeInactive && hasActiveElementalDamageTargets ? 'damage-contributing' : ''}`}
        >
            <Tooltip content={renderTooltipContent()} wrapperClassName="stat-current-tooltip">
                <div
                    className={`stat-current${showActiveTargetStyling && isGaugeInactive ? ' inactive' : ''}${!showActiveTargetStyling && displayValue < 0 ? ' negative' : ''}${!showActiveTargetStyling && isZeroValue ? ' zero' : ''}`}
                    style={
                        showActiveTargetStyling && !isGaugeInactive ? { color: current < 0 ? pctToColor(0) : pctToColor(targetPct) } : undefined
                    }
                >
                    {Number.isFinite(displayValue) ? displayValue : 0}{unit}
                </div>
            </Tooltip>
        </div>
    );

    // Weights column (stats panel toggle). The cell is rendered for every row
    // so the grid track stays aligned; only an active criterion carries a
    // weight to show.
    const weightBlock = !showWeight ? null : (
        <div className="stat-weight">
            {isRowActive ? (
                <CriterionWeight
                    value={weight}
                    onChange={(next) => onWeightChange?.(id, next)}
                    disabled={isGenerating || !canEditGenerator}
                    lang={lang}
                />
            ) : null}
        </div>
    );

    // Compact row — icon + value + label + (base). On mobile the whole row
    // is the select toggle; target/∞ editing lives in the gen bar. No gauge,
    // slider, desired input, or checkbox here. Read-only (compact desktop /
    // community) has no gauge to read the criteria off, so active ones get a
    // dot after the label rather than the editable view's label coloring.
    if (compactRow) {
        const compactRowClickable = mobileTargetCheckbox && canEditGenerator && !isGenerating;
        return (
            <div
                className={`stat-widget-row stat-widget-row--compact${showActiveTargetStyling && isRowActive ? ' stat-widget-row--active' : ''}${compactRowClickable ? ' stat-widget-row--clickable' : ''}`}
                role={compactRowClickable ? 'button' : undefined}
                tabIndex={compactRowClickable ? 0 : undefined}
                aria-pressed={compactRowClickable ? isRowActive : undefined}
                onClick={compactRowClickable ? handleMobileRowClick : undefined}
                onKeyDown={compactRowClickable ? handleMobileRowKeyDown : undefined}
            >
                <div className="stat-widget-row-main">
                    <img src={icon} alt={`${label_value} icon`} className="stat-icon" />
                    <div
                        className={`stat-info stat-info--compact ${showActiveTargetStyling && isMaximize ? 'stat-info--maximize' : ''} ${showActiveTargetStyling && hasActiveFiniteTarget ? 'stat-info--value-target' : ''}`}
                    >
                        <span className="stat-label">{stats.labelBesideValue(label_value)}</span>
                        {!showActiveTargetStyling && isRowActive ? (
                            <span className="stat-criterion-dot" aria-hidden="true">&bull;</span>
                        ) : null}
                    </div>
                    {additionalBlock}
                    {currentBlock}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`stat-widget-row${showActiveTargetStyling && isRowActive ? ' stat-widget-row--active' : ''}${mobileTargetCheckbox ? ' stat-widget-row--mobile-cb' : ''}${isRowClickable ? ' stat-widget-row--clickable' : ''}`}
            role={isRowClickable ? 'button' : undefined}
            tabIndex={isRowClickable ? 0 : undefined}
            onClick={isRowClickable ? handleRowClick : undefined}
            onKeyDown={isRowClickable ? handleStatInfoKeyDown : undefined}
        >
            {mobileTargetCheckbox && showGeneratorControls && (
                <label className="stat-widget-target-cb">
                    <input
                        type="checkbox"
                        checked={isRowActive}
                        disabled={isGenerating || !canEditGenerator}
                        onChange={handleTargetCheckboxChange}
                        aria-label={`${label_value}. ${TXT_STAT_TARGET_CB_ARIA[lang] || TXT_STAT_TARGET_CB_ARIA.en}`}
                    />
                </label>
            )}
            <div className="stat-widget-row-main">
            <div
                className={`stat-info ${isRowClickable ? 'stat-info--clickable' : 'stat-info--static'} ${showActiveTargetStyling && isMaximize ? 'stat-info--maximize' : ''} ${showActiveTargetStyling && hasActiveFiniteTarget ? 'stat-info--value-target' : ''}`}
            >
                <img src={icon} alt={`${label_value} icon`} className="stat-icon" />
                <span className="stat-label">{stats.labelBesideValue(label_value)}</span>
            </div>


            {additionalBlock}

            {currentBlock}

            {showGeneratorControls && (
            <div className="stat-params">
                <div
                    className={`gauge-container ${isGaugeInactive ? 'inactive' : ''} ${isMaximize ? 'stat-maximize' : ''} ${isPower && allowPower && isGaugeInactive && hasActiveElementalTargets ? 'power-contributing' : ''} ${isDamage && isGaugeInactive && hasActiveElementalDamageTargets ? 'damage-contributing' : ''}`}
                >
                    <div
                        className={`gauge-background ${isGaugeInactive ? 'inactive' : ''} ${isPower && allowPower && isGaugeInactive && hasActiveElementalTargets ? 'power-contributing' : ''} ${isDamage && isGaugeInactive && hasActiveElementalDamageTargets ? 'damage-contributing' : ''}`}
                    >
                        {/* Main stat gauge fill - shows total (current + power) for elemental stats when allowPower is enabled */}
                        <div
                            className={`gauge-fill ${isGaugeInactive ? 'inactive' : ''}`}
                            style={{
                                left: '0%',
                                width: `${gaugePct(effectiveCurrent - offNotch)}%`,
                                ...(!isGaugeInactive || isMaximize ? { background: pctToGaugeGradient(isMaximize ? 100 : targetPct) } : {}),
                            }}
                        />
                        {/* Power contribution gauge fill (semi-transparent overlay + Puissance icon) - elemental stats when allowPower is enabled */}
                        {allowPower && isElementalStat && powerValue > 0 && (
                            <div
                                className="gauge-fill-power"
                                style={{
                                    left: '0%',
                                    width: `${gaugePct(powerValue)}%`
                                }}
                            >
                                <img
                                    src={POWER_ICON}
                                    alt=""
                                    aria-hidden="true"
                                    className="gauge-fill-power-icon"
                                />
                            </div>
                        )}
                        {/* Damage contribution gauge fill (semi-transparent overlay) - for elemental damage stats */}
                        {isElementalDamageStat && damageValue > 0 && (
                            <div
                                className="gauge-fill-power"
                                style={{
                                    left: '0%',
                                    width: `${gaugePct(damageValue)}%`
                                }}
                            />
                        )}
                        {/* Critical damage contribution gauge fill (semi-transparent overlay) - for generic and elemental damage stats when enabled */}
                        {critDamCountsAsDamage && critDamageValue > 0 && (isElementalDamageStat || isDamage) && (
                            <div
                                className="gauge-fill-power"
                                style={{
                                    left: '0%',
                                    width: `${gaugePct(critDamageValue)}%`
                                }}
                            />
                        )}
                        <input
                            type="range"
                            className={`slider ${isGaugeInactive ? 'inactive' : ''}`}
                            min={offNotch}
                            max={maxRange}
                            value={Number.isFinite(sliderValue) ? sliderValue : offNotch}
                            onChange={handleSliderChange}
                            onPointerDown={handleSliderPointerDown}
                            onPointerUp={commitPending}
                            onKeyUp={commitPending}
                            onBlur={commitPending}
                            step={step}
                            disabled={isGenerating || !canEditGenerator}
                        />
                    </div>
                </div>
                <div ref={desiredRef} className={`desired-stat ${isGaugeInactive ? 'inactive' : ''}`}>
                    {isMaximize ? (
                        <button
                            type="button"
                            className="desired-stat-infinity"
                            aria-label={`${TXT_INFINITY_ARIA[lang] || TXT_INFINITY_ARIA.en}. ${TXT_INFINITY_TO_TARGET_ARIA[lang] || TXT_INFINITY_TO_TARGET_ARIA.en}`}
                            onClick={handleInfinityTargetClick}
                            disabled={isGenerating || !canEditGenerator}
                        >
                            {TXT_INFINITY[lang] || TXT_INFINITY.en}
                        </button>
                    ) : (
                        <NumberInput
                            value={hasTarget && Number.isFinite(sliderValue) ? sliderValue : ''}
                            onValueChange={handleTextBoxChange}
                            clearable
                            size={4}
                            min={intMinRange}
                            max={9999}
                            disabled={isGenerating || !canEditGenerator}
                        />
                    )}
                </div>
            </div>
            )}
            {weightBlock}
            </div>







        </div>
    );
};

export default React.memo(StatWidget);

